import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { X, Phone } from "lucide-react";
import { WindowIcon, RoofIcon, PaversIcon, MenuLines, HouseIcon, SolarIcon } from "./ServiceIcons";

const items = [
  {
    id: "ramen-wassen",
    title: "Ramen",
    to: "/diensten/ramen-wassen",
    Icon: WindowIcon,
  },
  {
    id: "dakreiniging",
    title: "Dak",
    to: "/diensten/dakreiniging",
    Icon: RoofIcon,
  },
  {
    id: "gevel-reiniging",
    title: "Gevel",
    to: "/diensten/gevel-reiniging",
    Icon: PaversIcon,
  },
];

const MobileBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const isActive = (to) => location.pathname === to;

  const handleNavToHome = (section) => {
    setMenuOpen(false);
    if (location.pathname === "/") {
      setTimeout(() => {
        document.getElementById(section)?.scrollIntoView({ behavior: "smooth" });
      }, 250);
    } else {
      navigate("/");
      setTimeout(() => {
        document.getElementById(section)?.scrollIntoView({ behavior: "smooth" });
      }, 400);
    }
  };

  return (
    <>
      {/* Bottom nav bar - mobile only */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-[0_-2px_10px_rgba(0,0,0,0.04)]"
        data-testid="mobile-bottom-nav"
      >
        <div className="px-2 pt-2 pb-[max(env(safe-area-inset-bottom),8px)]">
          <div className="grid grid-cols-4 gap-1">
            {items.map((item) => {
              const Icon = item.Icon;
              const active = isActive(item.to);
              return (
                <Link
                  key={item.id}
                  to={item.to}
                  className="flex flex-col items-center justify-center gap-1 py-1"
                  data-testid={`bottomnav-${item.id}`}
                >
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center border transition-all ${
                    active
                      ? "bg-sky-50 border-sky-300 text-sky-600"
                      : "bg-slate-50 border-slate-200 text-slate-700"
                  }`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <span className={`text-[11px] font-semibold leading-none ${active ? "text-sky-600" : "text-slate-700"}`}>{item.title}</span>
                </Link>
              );
            })}
            <button
              onClick={() => setMenuOpen(true)}
              className="flex flex-col items-center justify-center gap-1 py-1"
              data-testid="bottomnav-menu-btn"
            >
              <div className="w-11 h-11 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-700">
                <MenuLines className="w-6 h-6" />
              </div>
              <span className="text-[11px] font-semibold leading-none text-slate-700">Menu</span>
            </button>
          </div>
        </div>
      </div>

      {/* Spacer so content isn't hidden behind nav */}
      <div className="md:hidden h-24" aria-hidden="true" />

      {/* Drawer menu */}
      {menuOpen && (
        <div
          className="md:hidden fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm animate-fade-in"
          onClick={() => setMenuOpen(false)}
          data-testid="bottomnav-menu-overlay"
        >
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 shadow-2xl animate-fade-in-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Instrument Sans, sans-serif" }}>
                Menu
              </h3>
              <button
                onClick={() => setMenuOpen(false)}
                className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
                data-testid="bottomnav-menu-close"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>

            <div className="space-y-1">
              <Link
                to="/"
                onClick={() => { setMenuOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-sky-50 text-slate-800"
              >
                <span className="w-9 h-9 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-700">
                  <HouseIcon className="w-5 h-5" />
                </span>
                <span className="font-medium">Home</span>
              </Link>
              <Link
                to="/diensten/ramen-wassen"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-sky-50 text-slate-800"
              >
                <span className="w-9 h-9 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-700">
                  <WindowIcon className="w-5 h-5" />
                </span>
                <span className="font-medium">Ramen wassen</span>
              </Link>
              <Link
                to="/diensten/dakreiniging"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-sky-50 text-slate-800"
              >
                <span className="w-9 h-9 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-700">
                  <RoofIcon className="w-5 h-5" />
                </span>
                <span className="font-medium">Dakreiniging</span>
              </Link>
              <Link
                to="/diensten/zonnepaneel-schoonmaak"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-sky-50 text-slate-800"
              >
                <span className="w-9 h-9 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-700">
                  <SolarIcon className="w-5 h-5" />
                </span>
                <span className="font-medium">Zonnepaneel schoonmaak</span>
              </Link>
              <Link
                to="/diensten/gevel-reiniging"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-sky-50 text-slate-800"
              >
                <span className="w-9 h-9 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-700">
                  <PaversIcon className="w-5 h-5" />
                </span>
                <span className="font-medium">Oprit en gevelreiniging</span>
              </Link>
            </div>

            <div className="mt-5 pt-5 border-t border-slate-100 space-y-3">
              <button
                onClick={() => handleNavToHome("offerte")}
                className="w-full flex items-center justify-center gap-2 rounded-full py-3 bg-sky-500 hover:bg-sky-600 text-white font-semibold"
                data-testid="bottomnav-quote-btn"
              >
                Gratis offerte aanvragen
              </button>
              <a
                href="tel:0494614295"
                className="w-full flex items-center justify-center gap-2 rounded-full py-3 border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium"
              >
                <Phone className="w-4 h-4" />
                0494 61 42 95
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileBottomNav;
